import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/MainPage.page.css';
import { ensureValidSession, getAuthState } from '../utils/auth';
import CardNav from '../components/CardNav';
import RotatingText from '../components/RotatingText';
import ScrollReveal from '../components/ScrollReveal';
import DomeGallery from '../components/DomeGallery';

gsap.registerPlugin(ScrollTrigger);

const services = [
	{
		icon: (
			<svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
				<rect x="4" y="6" width="16" height="14" rx="2" strokeWidth="2" />
				<path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeWidth="2" />
				<line x1="8" y1="12" x2="16" y2="12" strokeWidth="2" />
				<line x1="8" y1="16" x2="16" y2="16" strokeWidth="2" />
			</svg>
		),
		title: 'Calderas / Autoclaves',
		text: 'Mantención, reparación, operación, limpieza de tubos y pasivado para Calderas y Autoclaves. Mantención integral a tu Sala de Calderas',
	},
	{
		icon: (
			<svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/>
			</svg>
		),
		title: 'Certificaciones',
		text: 'Preparación y apoyo para certificación SEREMI, salas de caldera y operadores.',
	},
	{
		icon: (
			<svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
				<path d="M13 10V3L4 14h7v7l9-11h-7z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
			</svg>
		),
		title: 'Equipos térmicos',
		text: 'Servicio técnico para equipos térmicos, tableros eléctricos de control y fuerza.',
	},
	{
		icon: (
			<svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
			</svg>
		),
		title: 'Soporte Técnico',
		text: 'Acompañamiento experto para resolver dudas técnicas, eficiencia, seguridad y mejora continua operativa.',
	},
];

const catalog = [
	{
		title: 'Calderas y Mantenimiento',
		items: ['Mantenimiento de calderas', 'Reparación de calderas', 'Retubado de calderas', 'Montaje de calderas', 'Normalización de salas de calderas', 'Operación de calderas', 'Técnico de calderas'],
	},
	{
		title: 'Quemadores y Combustión',
		items: ['Quemadores de calderas', 'Quemadores a gas', 'Quemadores diesel', 'Regulación de calderas', 'Regulación de combustión', 'Caldera a petróleo', 'Tren de gas', 'Reparación de quemadores'],
	},

	{
		title: 'Automatización y Control',
		items: ['Automatización de calderas', 'Automatización industrial', 'Automatización y control', 'Tableros eléctricos', 'Tableros de control', 'Tableros de fuerza', 'Monitoreo remoto', 'Monitoreo industrial', 'Monitoreo de calderas'],
	},
	{
		title: 'Certificación y Soporte',
		items: ['Certificación de calderas SEREMI - DS10', 'Certificación de salas de calderas', 'Cursos de operadores de caldera', 'Repuesto de calderas', 'Mantenimiento de bombas', 'Suministro de bombas y válvulas', 'Válvulas de seguridad', 'Tratamiento de agua', 'Ablandadores de agua', 'Estudio de eficiencia térmica', 'Montaje industrial'],
	},
];

const scadaAdvantages = [
	{
		title: 'Supervisión Centralizada',
		subtitle: 'Controla variables, alarmas y estados desde una sola interfaz operativa.',
	},
	{
		title: 'Respuesta Inmediata',
		subtitle: 'Detecta desviaciones a tiempo y actúa antes de una detención no planificada.',
	},
	{
		title: 'Trazabilidad Completa',
		subtitle: 'Registra históricos, eventos y tendencias para mejorar mantenimiento y seguridad.',
	},
	{
		title: 'Integración Escalable',
		subtitle: 'Conecta equipos, tableros y procesos en una arquitectura flexible y fácil de crecer.',
	},
];

const heroPhotos = ['/especialistas.png', '/calderasrojas.png', '/soldadores.png', '/limpieza.png'];

const catalogExpertise = [
	'Calderas / Autoclaves',
	'Automatización',
	'Quemadores',
	'Tableros Eléctricos',
	'Certificaciones',
	'Monitoreo / Control',
	'Soporte Industrial',
];

const whatsappUrl = 'https://wa.me/56993188356';
const instagramUrl = 'https://www.instagram.com/kermantenimiento/';
const seminarRegistrationUrl = 'https://uchile.cl/agenda/243100/seminario-mineria--medio-ambiente-desafios-ambientales-sector-minero';

function getRecaptchaToken(siteKey) {
	return new Promise((resolve, reject) => {
		const deadline = Date.now() + 10000;

		const execute = () => {
			const recaptcha = window.grecaptcha;
			if (!recaptcha?.ready || !recaptcha.execute) {
				if (Date.now() < deadline) {
					setTimeout(execute, 50);
					return;
				}
				reject(new Error('reCAPTCHA no terminó de cargar'));
				return;
			}

			try {
				recaptcha.ready(() => {
					try {
						Promise.resolve(recaptcha.execute(siteKey, { action: 'contact' })).then(resolve).catch(reject);
					} catch (error) {
						reject(error);
					}
				});
			} catch (error) {
				reject(error);
			}
		};

		execute();
	});
}

export default function MainPage() {
	const navigate = useNavigate();
	const scadaSectionRef = useRef(null);
	const scadaCardRefs = useRef([]);
	const scadaFillRefs = useRef([]);
	const [, setMenuOpen] = useState(false);
	const [contactOpen, setContactOpen] = useState(false);
	const [catalogOpen, setCatalogOpen] = useState(null);
	const [formMessage, setFormMessage] = useState(null);
	const [formLoading, setFormLoading] = useState(false);
	const [isCheckingSession, setIsCheckingSession] = useState(false);
	const [scadaVideoOpen, setScadaVideoOpen] = useState(false);

	useEffect(() => {
		document.title = 'KER Mantenimiento Spa';

		let descriptionMeta = document.querySelector('meta[name="description"]');
		if (!descriptionMeta) {
			descriptionMeta = document.createElement('meta');
			descriptionMeta.setAttribute('name', 'description');
			document.head.appendChild(descriptionMeta);
		}
		descriptionMeta.setAttribute('content', 'KER Mantenimiento Spa: calderas, automatización industrial, monitoreo remoto, SCADA, certificaciones y soporte técnico.');
	}, []);

	useEffect(() => {
		const section = scadaSectionRef.current;
		const cards = scadaCardRefs.current.filter(Boolean);

		if (!section || !cards.length) return undefined;

		const desktopScadaQuery = window.matchMedia('(min-width: 769px) and (pointer: fine)');
		let ctx;

		const setupScadaTimeline = () => {
			if (ctx) {
				ctx.revert();
				ctx = null;
			}

			if (!desktopScadaQuery.matches) {
				ScrollTrigger.refresh();
				return;
			}

			ctx = gsap.context(() => {
				gsap.set(cards, { autoAlpha: 0, yPercent: 10 });
				gsap.set(cards[0], { autoAlpha: 1, yPercent: 0 });

				const tl = gsap.timeline({
					scrollTrigger: {
						trigger: section,
						start: 'top top',
						end: 'bottom bottom',
						scrub: 0.5,
						invalidateOnRefresh: true,
					},
					defaults: { ease: 'none' },
				});

				cards.forEach((card, index) => {
					const fillPair = scadaFillRefs.current[index] || {};
					const titleFill = fillPair.title;
					const subtitleFill = fillPair.subtitle;
					const nextCard = cards[index + 1];

					tl.to(card, { autoAlpha: 1, yPercent: 0, duration: 0.01 });
					if (titleFill) {
						tl.to(titleFill, { clipPath: 'inset(0 0% 0 0)', duration: 0.85 }, '<');
					}
					if (subtitleFill) {
						tl.to(subtitleFill, { clipPath: 'inset(0 0% 0 0)', duration: 1.05 }, '<0.15');
					}
					tl.to({}, { duration: 0.35 });
					if (nextCard) {
						tl.to(card, { autoAlpha: 0, yPercent: -10, duration: 0.45 });
						tl.fromTo(nextCard, { autoAlpha: 0, yPercent: 10 }, { autoAlpha: 1, yPercent: 0, duration: 0.45 }, '<');
					}
				});

			}, section);
		};

		setupScadaTimeline();

		const handleMediaChange = () => {
			setupScadaTimeline();
		};

		if (desktopScadaQuery.addEventListener) {
			desktopScadaQuery.addEventListener('change', handleMediaChange);
		} else {
			desktopScadaQuery.addListener(handleMediaChange);
		}

		return () => {
			if (desktopScadaQuery.removeEventListener) {
				desktopScadaQuery.removeEventListener('change', handleMediaChange);
			} else {
				desktopScadaQuery.removeListener(handleMediaChange);
			}
			if (ctx) {
				ctx.revert();
			}
		};
	}, []);

	async function handleIngresar(event) {
		event.preventDefault();

		if (isCheckingSession) {
			return;
		}

		setIsCheckingSession(true);
		const session = await ensureValidSession();
		setIsCheckingSession(false);

		if (!session?.ok) {
			navigate('/login');
			return;
		}

		const auth = getAuthState();

		if (auth.isAdmin) {
			navigate('/admin');
			return;
		}

		navigate('/dashboard');
	}

	async function handleSubmit(e) {
		e.preventDefault();
		const form = e.target;
		const name = (form.nombre?.value || form.name?.value || '').trim();
		const email = (form.correo?.value || form.email?.value || '').trim();
		const phone = (form.telefono?.value || form.phone?.value || '').trim();
		const company = (form.empresa?.value || form.company?.value || '').trim();
		const message = (form.mensaje?.value || form.message?.value || '').trim();

		if (message && message.length < 10) return setFormMessage({ text: 'El mensaje debe tener al menos 10 caracteres.', type: 'error' });
		if (!name || !company || !phone || !email || !message) return setFormMessage({ text: 'Completa todos los campos antes de enviar.', type: 'error' });

		setFormLoading(true);
		setFormMessage(null);
		try {
			const rawSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
			const siteKey = rawSiteKey.replace(/^['"]|['"]$/g, '').trim();
			if (!siteKey) {
				setFormLoading(false);
				setFormMessage({ text: 'Captcha no configurado. Contacta al administrador.', type: 'error' });
				return;
			}
			let captchaToken;
			try {
				captchaToken = await getRecaptchaToken(siteKey);
			} catch (error) {
				console.error('Error al obtener el token de reCAPTCHA:', error);
				setFormLoading(false);
				setFormMessage({ text: 'No se pudo verificar el CAPTCHA. Recarga la página e inténtalo nuevamente.', type: 'error' });
				return;
			}
			const rawBase = process.env.REACT_APP_API_BASE_URL || '';
			const base = rawBase.replace(/\/$/, '');
			const url = `${base}/api/contact`;
			const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, email, phone, company, message, captchaToken }) });
			const data = await res.json().catch(() => ({}));
			setFormLoading(false);
			if (!res.ok) {
				setFormMessage({ text: data.error || data.message || 'Error al enviar el mensaje, intenta nuevamente.', type: 'error' });
				return;
			}
			setFormMessage({ text: data.message || '¡Mensaje enviado! Nos contactaremos pronto.', type: 'success' });
			form.reset();
			setTimeout(() => setFormMessage(null), 5000);
		} catch (err) {
			console.error('Error al enviar el formulario de contacto:', err);
			setFormLoading(false);
			setFormMessage({ text: 'No se pudo conectar con el servidor. Intenta más tarde.', type: 'error' });
		}
	}

	return (
		<div className="ker-page">
			<CardNav
				position="left"
				logo="/logo512.png"
				logoAlt="KER"
				items={[
					{
						label: 'Navegación',
						bgColor: 'transparent',
						backdropfilter: 'blur',
						textColor: '#ffffff',
						links: [
							{ label: 'Inicio', href: '#inicio', ariaLabel: 'Ir a inicio' },
							{ label: 'Servicios', href: '#servicios', ariaLabel: 'Ir a servicios' },
							{ label: 'Detalle de Servicios', href: '#catalogo', ariaLabel: 'Ir a catálogo' },
							{ label: 'Sistema SCADA', href: '#monitoreo', ariaLabel: 'Ir a monitoreo' },
						],
					},
					{
						label: 'Empresa',
						bgColor: 'transparent',
						backdropfilter: 'blur',
						textColor: '#ffffff',
						links: [
							{ label: 'Socio Clave', href: '#socio', ariaLabel: 'Ir a socio clave' },
							{ label: 'Quiénes Somos', href: '#nosotros', ariaLabel: 'Ir a quienes somos' },
							{ label: 'Más de nosotros', href: '#mas-de-nosotros', ariaLabel: 'Ir a más de nosotros' },
						],
					},
					{
						label: 'Contacto',
						bgColor: 'transparent',
						backdropfilter: 'blur',
						textColor: '#ffffff',
						links: [
							{ label: 'Contacto', href: '#contacto', ariaLabel: 'Ir a contacto' },
							{ label: isCheckingSession ? 'Validando...' : 'Ingresar', onClick: handleIngresar, ariaLabel: 'Ingresar al sistema' },
						],
					},
				]}
				onLogin={handleIngresar}
				loginLabel={isCheckingSession ? 'Validando...' : 'Ingresar'}
				baseColor="#120b08"
				buttonBgColor="#eb673b"
				buttonTextColor="#ffffff"
				menuColor="#ffffff"
				openMenuColor="#ffffff"
				accentColor="#eb673b"
			/>



			{false && (
			<div className="mobile-menu">
				<a href="#inicio" onClick={() => setMenuOpen(false)}>Inicio</a>
				<a href="#servicios" onClick={() => setMenuOpen(false)}>Servicios</a>
				<a href="#monitoreo" onClick={() => setMenuOpen(false)}>Monitoreo</a>
				<a href="#catalogo" onClick={() => setMenuOpen(false)}>Catálogo</a>
				<a href="#socio" onClick={() => setMenuOpen(false)}>Socio Clave</a>
				<a href="#nosotros" onClick={() => setMenuOpen(false)}>Quiénes Somos</a>
				<a href="#mas-de-nosotros" onClick={() => setMenuOpen(false)}>Más de nosotros</a>
				<a href="#contacto" onClick={() => setMenuOpen(false)}>Contacto</a>
				<button type="button" onClick={handleIngresar} disabled={isCheckingSession}>{isCheckingSession ? 'Validando...' : 'Ingresar'}</button>
				<button className="mobile-close" type="button" onClick={() => setMenuOpen(false)} aria-label="Cerrar menú">×</button>
			</div>
			)}

			<button className="float-btn" type="button" onClick={() => setContactOpen((open) => !open)} aria-label="Contacto">☎</button>
			<div className={`contact-panel ${contactOpen ? 'show' : ''}`}>
				<h3>Contáctanos</h3>
				<div className="contact-panel-item">
					<span className="contact-panel-item-icon" aria-hidden="true">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.4 19.4 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.4 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2Z" />
						</svg>
					</span>
					<a href={whatsappUrl} target="_blank" rel="noopener noreferrer" aria-label="Abrir WhatsApp con KER">
						+56 9 9318 8356
					</a>
				</div>
				<div className="contact-panel-item">
					<span className="contact-panel-item-icon" aria-hidden="true">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.4 19.4 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.4 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2Z" />
						</svg>
					</span>
					<span>+56 9 7389 1406</span>
				</div>
				<div className="contact-panel-item">
					<span className="contact-panel-item-icon" aria-hidden="true">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<path d="M4 4h16v16H4V4z" />
							<path d="m4 8 8 5 8-5" />
						</svg>
					</span>
					<span>contacto@ker.cl</span>
				</div>
				<div className="contact-panel-item">
					<span className="contact-panel-item-icon" aria-hidden="true">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<path d="M4 4h16v16H4V4z" />
							<path d="m4 8 8 5 8-5" />
						</svg>
					</span>
					<span>karina@ker.cl</span>
				</div>
				<div className="contact-panel-item">
					<span className="contact-panel-item-icon" aria-hidden="true">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<path d="M12 2a7 7 0 0 1 7 7c0 5.25-7 13-7 13S5 14.25 5 9a7 7 0 0 1 7-7z" />
							<circle cx="12" cy="9" r="2.5" />
						</svg>
					</span>
					<span>Santiago - Antofagasta</span>
				</div>
				<div className="contact-panel-item">
					<span className="contact-panel-item-icon" aria-hidden="true">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<rect x="3" y="3" width="18" height="18" rx="5" />
							<circle cx="12" cy="12" r="4" />
							<circle cx="17.4" cy="6.6" r="0.8" fill="currentColor" stroke="none" />
						</svg>
					</span>
					<a href={instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="Abrir Instagram de KER">
						@kermantenimiento
					</a>
				</div>
			</div>

			<main>
				<section id="inicio" className="hero-section">
					<video className="hero-video" autoPlay muted loop playsInline preload="metadata" src="/logoindustrial.mp4" />
					<div className="hero-overlay" />
					<div className="fade-up hero-copy visible">
						<h1>MANTENIMIENTO INDUSTRIAL</h1>
						<p>Soluciones integrales para la continuidad operativa de tu industria.</p>
						<div className="hero-actions">
							
							<a href="#servicios">Ver Servicios</a>
						</div>
					</div>
					<div className="hero-photos">
						{heroPhotos.map((photo) => (
							<img key={photo} src={photo} alt="" loading="lazy" />
						))}
					</div>
				</section>

				<section id="servicios" className="section-shell services-section">
					<div className="section-title">
						<h2>NUESTROS SERVICIOS</h2>
						<p>Atención técnica especializada para equipos industriales, sistemas térmicos, automatización y continuidad operacional.</p>
					</div>
					<div className="service-grid">
						{services.map((service) => (
							<article className="service-card" key={service.title}>
								<div className="service-icon">{service.icon}</div>
								<h3>{service.title}</h3>
								<p>{service.text}</p>
							</article>
						))}
					</div>
				</section>

				<section id="catalogo" className="section-shell catalog-section">
					<div className="section-title">
						<h2>DETALLE DE SERVICIOS</h2>
						<p className="catalog-rotating-copy">
							<RotatingText
								prefix="Expertos en"
								texts={catalogExpertise}
								rotationInterval={2000}
								splitBy="characters"
							/>
						</p>
					</div>
					<div className="catalog-grid">
						{catalog.map((group, index) => {
							const isOpen = catalogOpen === index;
							return (
								<div className="catalog-item" key={group.title}>
									<button type="button" onClick={() => setCatalogOpen(isOpen ? null : index)}>
										<span>{group.title}</span>
										<b className={isOpen ? 'open' : ''}>\/</b>
									</button>
									<div className={`catalog-content ${isOpen ? 'open' : ''}`}>
										<ul>
											{group.items.map((item) => <li key={item}>{item}</li>)}
										</ul>
									</div>
								</div>
							);
						})}
					</div>
				</section>

				<section
					id="monitoreo"
					ref={scadaSectionRef}
					className="scada-section"
					style={{ '--scada-scroll-height': `${100 + (scadaAdvantages.length * 105)}vh` }}
				>
					<div className="scada-sticky">
						<div className="scada-shell">
							<div className="section-title scada-section-title">
								<h2>SISTEMA SCADA</h2>
								<p>Monitoreo industrial para visualizar datos críticos, alarmas y estados operativos con claridad.</p>
							</div>

							<div className="scada-layout">
								<div className="scada-video-card">
									<div className="scada-video-frame">
										<video autoPlay muted loop playsInline preload="metadata" src="/videoscada.mp4" />
										<div className="scada-video-glow" />
									</div>
									<button type="button" className="scada-video-button" onClick={() => setScadaVideoOpen(true)}>
										Ver video completo
									</button>
								</div>

								<div className="scada-text-overlay">
									{scadaAdvantages.map((item, index) => (
										<div
											className="scada-advantage"
											key={item.title}
											ref={(node) => {
												scadaCardRefs.current[index] = node;
											}}
										>
											<div className="scada-advantage-inner">
											<ScrollReveal
												variant="title"
												disableAnimation
												style={{ fontSize: 'clamp(1.75rem, 4vw, 3.65rem)', letterSpacing: '0' }}
												baseColor="rgba(235, 103, 59, 0.22)"
												fillColor="#eb673b"
												fillRef={(node) => {
													scadaFillRefs.current[index] = {
														...(scadaFillRefs.current[index] || {}),
														title: node,
													};
												}}
											>
												{item.title}
											</ScrollReveal>
											<ScrollReveal
												variant="subtitle"
												disableAnimation
												style={{ fontSize: 'clamp(1.05rem, 1.65vw, 1.35rem)', letterSpacing: '0' }}
												baseColor="rgba(79, 82, 90, 0.26)"
												fillColor="#4f525a"
												fillRef={(node) => {
													scadaFillRefs.current[index] = {
														...(scadaFillRefs.current[index] || {}),
														subtitle: node,
													};
												}}
											>
												{item.subtitle}
											</ScrollReveal>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				</section>

				{scadaVideoOpen && (
					<div className="scada-modal" role="dialog" aria-modal="true" aria-label="Video Sistema SCADA">
						<button type="button" className="scada-modal-backdrop" aria-label="Cerrar video" onClick={() => setScadaVideoOpen(false)} />
						<div className="scada-modal-player">
							<button type="button" className="scada-modal-close" onClick={() => setScadaVideoOpen(false)} aria-label="Cerrar video">
								×
							</button>
							<video controls autoPlay playsInline src="/videoscada.mp4" />
						</div>
					</div>
				)}

				<section id="socio" className="partner-section">
				<div className="partner-section-shell">
					<div className="partner-images partner-images-left">
						<img src="/valvula 1.png" alt="Válvula industrial" />
						<img src="/valvula 2.png" alt="Válvula industrial" />
					</div>
					<div className="partner-card">
						<h2>SOCIO CLAVE</h2>
						<p>Contamos con un socio estratégico para la obtención de repuestos específicos de calderas y equipos industriales.</p>
						<div className="partner-tags">
							<img src="/2vias_v1_gota.png" alt="Dos Vías" className="partner-logo" />
								<div className="partner-benefits">
									<span>✓ Repuestos originales y certificados</span>
									<span>✓ Garantía de calidad y durabilidad</span>
								</div>
						</div>
						<div className="partner-action partner-action-centered">
							<a href="https://dosvias.cl" target="_blank" rel="noopener noreferrer">Visitar sitio web</a>
						</div>
					</div>
					<div className="partner-images partner-images-right">
						<img src="/valvula3.png" alt="Válvula industrial" />
						<img src="/valvula4.png" alt="Válvula industrial" />
						</div>
					</div>
				</section>

				<section id="nosotros" className="section-shell about-section">
					<div>
						<h2 className="about-title-section">QUIÉNES SOMOS</h2>
					</div>
					<div className="about-grid">
						<article>
							<h3>Equipo Técnico</h3>
							<p>Contamos con un equipo profesional altamente calificado, con amplia experiencia en el sector industrial, comprometido con la seguridad y la productividad de nuestros clientes.</p>
						</article>
						<article>
							<h3>Nuestra Misión</h3>
							<p>Proporcionar mantenimiento y soluciones de alta calidad, ayudando a nuestros clientes a mejorar su productividad, creando ventaja competitiva y garantizando seguridad, eficiencia y responsabilidad.</p>
						</article>
						<article>
							<h3>Nuestro equipo</h3>
							<p>KER está constituido por un equipo de profesionales con más de 15 años de experiencia, inspirados en entregar valor con innovación y eficiencia, ofreciendo un servicio excepcional para la industria.</p>
						</article>

						

					</div>
					<div className="stats-grid">
						<div><strong>15+</strong><span>Años de experiencia</span></div>
						<div><strong>100%</strong><span>Cobertura nacional</span></div>
						<div><strong>24/7</strong><span>Soporte disponible</span></div>
						<div><strong>100%</strong><span>Clientes satisfechos</span></div>
					</div>
				</section>

				<section id="clientes" className="section-shell clients-section">
					<div className="section-title">
						<h2>QUIÉNES CONFÍAN EN NOSOTROS</h2>
					</div>
					<div className="client-gallery-wrapper">
						<div className="client-gallery-container">
							<DomeGallery
								minRadius={650}
								segments={28}
								grayscale={false}
							/>
							</div>
						</div>
				</section>

				<section id="mas-de-nosotros" className="section-shell news-section" aria-labelledby="news-title">
					<div className="section-title news-heading">
						<h2 id="news-title">KER EN ACCIÓN</h2>
					</div>

					<article className="news-card">
						<div className="news-poster-column">
							<a className="news-poster-link" href="/seminario.png" target="_blank" rel="noopener noreferrer" aria-label="Abrir afiche del seminario en tamaño completo">
								<img src="/seminario.png" alt="Afiche del Seminario Minería y Medio Ambiente: Desafíos Ambientales del Sector Minero frente al 2030" loading="lazy" />
							</a>
							<a className="news-image-action" href="/seminario.png" target="_blank" rel="noopener noreferrer">Ver afiche en tamaño completo</a>
						</div>

						<div className="news-content">
							<p className="news-lead">
								La <strong>Facultad de Gobierno de la Universidad de Chile</strong>, en el marco de sus{' '}
								<a href="https://diplomasuchile.cl/gestion-ambiental-para-el-sector-minero" target="_blank" rel="noopener noreferrer"><strong>Diplomas de Postítulo en Gestión Ambiental para el Sector Minero</strong></a>{' '}
								y <a href="https://diplomasuchile.cl/gestion-estrategica-de-permisos-ambientales-y-sectoriales-en-chile" target="_blank" rel="noopener noreferrer"><strong>Gestión Estratégica de Permisos Ambientales y Sectoriales en Chile</strong></a>, invita a la comunidad académica, profesionales y personas interesadas al <strong><em>Seminario Minería &amp; Medio Ambiente: Desafíos Ambientales del Sector Minero frente al 2030</em></strong>.
							</p>
							<p>En esta actividad se analizarán las principales transformaciones, exigencias regulatorias y perspectivas de sostenibilidad que enfrentará la industria minera en la presente década, desde la mirada del sector público, el mundo corporativo, las consultoras ambientales y el territorio.</p>

							<div className="news-details" aria-label="Datos de la actividad">
								<h3>Datos de la actividad</h3>
								<dl>
									<div><dt>Fecha</dt><dd>Jueves 20 de agosto</dd></div>
									<div><dt>Horario</dt><dd>09:30 a 12:30 h</dd></div>
									<div><dt>Lugar</dt><dd>Auditorio VM20, Facultad de Gobierno, Universidad de Chile (Ramón Carnicer #15, Piso 7, Providencia).</dd></div>
									<div><dt>Modalidad</dt><dd>Presencial / Online</dd></div>
								</dl>
								<a className="news-register-button" href={seminarRegistrationUrl} target="_blank" rel="noopener noreferrer">Inscripción gratuita</a>
							</div>

							<div className="news-speakers">
								<h3>Expositores y temáticas</h3>
								<div className="news-speaker-list">
									<div><em>Desafíos en Sostenibilidad para el sector público</em><p><strong>Salustio Montalva</strong>: Jefe del Departamento Jurídico de SERNAGEOMIN.</p></div>
									<div><em>Desafíos para Empresas Mineras</em><p><strong>Jorge Brower</strong>: Abogado Ambiental y Regulatorio en Capstone Copper Chile.</p></div>
									<div><em>Visión de las Consultoras Ambientales</em><p><strong>Andrés Camaño</strong>: Director de Consultora Socio-Ecológicos.</p></div>
									<div><em>Visión Integrada al Territorio</em><p><strong>Manuel Silva</strong>: Representante del Colegio de Geógrafos de Chile.</p></div>
								</div>
							</div>
						</div>
					</article>
				</section>

			<section id="contacto" className="py-24 bg-gray-50">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="text-center max-w-3xl mx-auto mb-16">
						<h2 className="about-title-section">CONTÁCTANOS</h2>
						
						<p className="text-lg text-gray-600 mt-6">Atendemos requerimientos de mantenimiento de calderas industriales, equipos termicos, certificaciones y soporte para empresas en distintas regiones de Chile.</p>
					</div>
					<div className="grid lg:grid-cols-2 gap-12">
						<div className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl">
							<h3 className="font-heading font-bold text-2xl text-gray-corporate mb-6">Solicita una evaluación</h3>
							<form id="contactForm" className="space-y-6" onSubmit={handleSubmit}>
								<div className="grid sm:grid-cols-2 gap-6">
									<div>
										<label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
										<input type="text" id="nombre" name="nombre" required className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-primary focus:ring-2 focus:ring-orange-primary/20 outline-none transition-all" placeholder="Nombre..." />
									</div>
									<div>
										<label htmlFor="empresa" className="block text-sm font-medium text-gray-700 mb-2">Empresa</label>
										<input type="text" id="empresa" name="empresa" required className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-primary focus:ring-2 focus:ring-orange-primary/20 outline-none transition-all" placeholder="Empresa..." />
									</div>
								</div>
								<div className="grid sm:grid-cols-2 gap-6">
									<div>
										<label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
										<input type="tel" id="telefono" name="telefono" required className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-primary focus:ring-2 focus:ring-orange-primary/20 outline-none transition-all" placeholder="+56 9 1234 5678" />
									</div>
									<div>
										<label htmlFor="correo" className="block text-sm font-medium text-gray-700 mb-2">Correo</label>
										<input type="email" id="correo" name="correo" required className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-primary focus:ring-2 focus:ring-orange-primary/20 outline-none transition-all" placeholder="Correo..." />
									</div>
								</div>
								<div>
									<label htmlFor="mensaje" className="block text-sm font-medium text-gray-700 mb-2">Mensaje</label>
									<textarea id="mensaje" name="mensaje" rows="4" required className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-primary focus:ring-2 focus:ring-orange-primary/20 outline-none transition-all resize-none" placeholder="Ej: Solicito cotización para caldera, autoclave y reparación de equipos térmicos."></textarea>
								</div>
								<button type="submit" id="submitBtn" className="w-full btn-primary text-white px-8 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 border-0" disabled={formLoading} style={{ border: '1px solid transparent' }}>
									{formLoading ? (
										<>
											<svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
												<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
											</svg>
											<span>Enviando...</span>
										</>
									) : (
										<>
											<span>Enviar solicitud</span>
											<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
										</>
									)}
								</button>
								<div
									id="formMessage"
									className={`${formMessage ? `text-center p-4 rounded-xl ${formMessage.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}` : 'hidden text-center p-4 rounded-xl'}`}
								>
									{formMessage ? (
										formMessage.type === 'success' ? (
											<div className="flex items-center justify-center gap-2">
												<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/></svg>
												<span>{formMessage.text}</span>
											</div>
										) : (
											<span>{formMessage.text}</span>
										)
									) : null}
								</div>
							</form>
						</div>

						<div className="space-y-8 h-full">
							<div className="contact-info-card">
								<h3 className="font-heading font-bold text-4xl mb-6">Información de contacto</h3>
								<div className="contact-info-grid">
									<div className="contact-info-item">
										<div className="contact-info-icon">
											<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
										</div>
										<div>
											<h4 className="font-semibold mb-1">Teléfonos</h4>
											<p><a href={whatsappUrl} target="_blank" rel="noopener noreferrer">+56 9 9318 8356</a></p>
											<p>+56 9 7389 1406</p>
										</div>
									</div>
									<div className="contact-info-item">
										<div className="contact-info-icon">
											<svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
												<path d="M20.5 11.8a8.5 8.5 0 0 1-12.6 7.4L3 21l1.7-4.7a8.5 8.5 0 1 1 15.8-4.5Z" />
												<path d="M8.2 7.8c.3-.7.6-.7 1-.7h.3c.2 0 .4 0 .6.5l.8 1.9c.1.3.1.5-.1.7l-.6.8c-.2.2-.3.4-.1.7.6 1.1 1.5 2 2.6 2.6.3.2.5.1.7-.1l.8-1c.2-.3.5-.3.8-.2l1.8.9c.3.2.5.3.5.5 0 .2-.1 1.2-.7 1.8-.6.6-1.5.9-2.4.7-1.1-.2-2.5-.7-4.2-2.2-2-1.8-3.2-4-3.3-5.3 0-.7.2-1.2.5-1.6Z" />
											</svg>
										</div>
										<div>
											<h4 className="font-semibold mb-1">WhatsApp</h4>
											<p><a href={whatsappUrl} target="_blank" rel="noopener noreferrer">+56 9 9318 8356</a></p>
										</div>
									</div>
									<div className="contact-info-item">
										<div className="contact-info-icon">
											<svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
												<rect x="3" y="3" width="18" height="18" rx="5" />
												<circle cx="12" cy="12" r="4" />
												<circle cx="17.4" cy="6.6" r="0.8" fill="currentColor" stroke="none" />
											</svg>
										</div>
										<div>
											<h4 className="font-semibold mb-1">Instagram</h4>
											<p><a href={instagramUrl} target="_blank" rel="noopener noreferrer">@kermantenimiento</a></p>
										</div>
									</div>
									<div className="contact-info-item">
										<div className="contact-info-icon">
											<svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
												<path d="M4 4h16v16H4V4z" />
											<path d="M4 8l8 5 8-5" />
										</svg>
										</div>
										<div>
											<h4 className="font-semibold mb-1">Correos</h4>
											<p>contacto@ker.cl</p>
											<p>karina@ker.cl</p>
										</div>
									</div>
									<div className="contact-info-item">
										<div className="contact-info-icon">
											<svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
												<path d="M12 2a7 7 0 0 1 7 7c0 5.25-7 13-7 13S5 14.25 5 9a7 7 0 0 1 7-7z" />
											<circle cx="12" cy="9" r="2.5" />
										</svg>
										</div>
										<div>
											<h4 className="font-semibold mb-1">Cobertura Nacional</h4>
											<p>Oficina central Santiago</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
			</main>

			<footer className="page-footer">
				<p>© 2026 KER Mantenimiento Spa - Santiago & Antofagasta, Chile</p>
			</footer>
		</div>
	);
}
